import { Table } from 'antd';
import { SearchX, ShieldAlert, Tags } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { buildProposalColumns } from './build-proposal-columns';
import { EditProposalDrawer } from './EditProposalDrawer';
import { ProposalReasonModal } from './ProposalReasonModal';
import type { RateCardView, RateLineProposalView } from '../../../rate-cards.api';
import { useCardProposals } from '../hooks/use-card-proposals';
import { useProposalActions } from '../hooks/use-proposal-actions';
import { useRateCardPermissions } from '../../../hooks/use-rate-card-permissions';

/** This card's proposals: read for anyone who may see them, and the author's own edit, submit and withdraw on a draft. Decisions are the approval queue's. */
export function ProposalsTab({ card }: { card: RateCardView }) {
  const { list, state, isRefreshing, reload } = useCardProposals(card.id);
  const { userId, canEdit } = useRateCardPermissions();
  const actions = useProposalActions(reload);

  const canActOn = (proposal: RateLineProposalView) => canEdit && card.isActive && proposal.authorId === userId;
  const columns = buildProposalColumns({ list, canActOn, onAction: actions.open });

  if (state.status === 'loading') return <TableSkeleton label="Loading proposals" />;
  if (state.status !== 'loaded') {
    return (
      <EmptyState
        icon={ShieldAlert}
        title="Couldn't load the proposals"
        description="Something went wrong loading this card's proposals. Try reloading the page."
      />
    );
  }

  const empty = list.isFiltered ? (
    <EmptyState icon={SearchX} title="No matching proposals" description="No proposals match these filters." />
  ) : (
    <EmptyState icon={Tags} title="No proposals yet" description="A new line or a price change starts here as a draft until it is approved." />
  );

  const { action } = actions;

  return (
    <>
      <Table
        rowKey="id"
        dataSource={state.page.items}
        columns={columns}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: empty }}
        {...list.tableProps<RateLineProposalView>(state.page.total)}
      />
      <EditProposalDrawer proposal={action?.kind === 'edit' ? action.proposal : null} onSaved={reload} onClose={actions.close} />
      {action?.kind === 'submit' && (
        <ProposalReasonModal
          open
          title="Submit for approval"
          confirmLabel="Submit"
          onConfirm={(reason) => actions.submit(action.proposal, reason)}
          onClose={actions.close}
        />
      )}
      {action?.kind === 'withdraw' && (
        <ProposalReasonModal
          open
          title="Withdraw this draft"
          confirmLabel="Withdraw"
          isDanger
          onConfirm={(reason) => actions.withdraw(action.proposal, reason)}
          onClose={actions.close}
        />
      )}
    </>
  );
}
